export const SUPPORTED_PRODUCT_CATALOG_SCHEMA_VERSION = "1.0";

export type ProductCatalogPrice = {
  amount: number | null;
  currency: string;
  billingPeriod: string | null;
  taxIncluded: boolean | null;
  purchasable: boolean;
  displayLabel: string;
};

export type ProductCatalogQuota = {
  key: string;
  displayName: string;
  value: number | null;
  displayUnit: string;
  period: string | null;
  displayPeriod: string | null;
  displayMode: "limited" | "none" | "effectively_unlimited";
  displayLabel: string;
};

export type ProductCatalogFrequency = { key: string; displayName: string };

export type ProductCatalogFeature = {
  key: string;
  displayName: string;
  description: string;
  enabled: boolean;
  quotas: Record<string, ProductCatalogQuota>;
  subscription?: {
    enabled: boolean;
    availableFrequencies: ProductCatalogFrequency[];
  };
};

export type ProductCatalogPlan = {
  key: string;
  displayName: string;
  description: string;
  price: ProductCatalogPrice;
  features: Record<string, ProductCatalogFeature>;
};

export type ProductCatalogPlanKey = "free" | "invite_trial" | "standard" | "executive";
export type ProductCatalogFeatureKey =
  | "chat"
  | "group_talk"
  | "company_research"
  | "news"
  | "report"
  | "receipt_ocr"
  | "memo_ocr"
  | "google_integration";

export type ProductCatalogNamedValue = { key: string; displayName: string };

export type ProductCatalog = {
  schemaVersion: string;
  plans: ProductCatalogPlan[];
  capabilities: {
    knowledgeLibrary: {
      displayName: string;
      description: string;
      availablePlanKeys: string[];
      availableOnAllPublicPlans: boolean;
      aiReferenceableSourceTypes: ProductCatalogNamedValue[];
      referenceRequirements: {
        requiresAiReadyState: boolean;
        displayDescription: string;
      };
      pdf: {
        displayName: string;
        maxFileSizeBytes: number;
        maxFileSizeMb: number;
        maxPages: number;
      };
    };
    groupTalk: {
      displayName: string;
      description: string;
      availablePlanKeys: string[];
      maxKnowledgeItems: number;
      knowledgeSelectionTiming: ProductCatalogNamedValue;
      knowledgeChangeDuringSession: boolean;
      knowledgeReusableFromLibrary: boolean;
      userInputMaxCharacters: number;
    };
    calendar: {
      displayName: string;
      availablePlanKeys: string[];
      availableOnAllPublicPlans: boolean;
      usesChatQuota: boolean;
      readOnly: boolean;
      sourceScope: {
        mainCalendarOnly: boolean;
        supportedSources: ProductCatalogNamedValue[];
        unsupportedSources: ProductCatalogNamedValue[];
      };
      supportedRanges: ProductCatalogNamedValue[];
      unsupportedActions: ProductCatalogNamedValue[];
      connectionGuidance: {
        settingsLocation: string;
        doNotAssumePlanShortage: boolean;
        distinguishFailureStates: ProductCatalogNamedValue[];
        hideTechnicalPermissionNames: boolean;
      };
    };
    executiveMemo: {
      displayName: string;
      maxImagesPerMemo: number;
      quotaDisplayUnit: string;
    };
  };
  notices: Record<string, string>;
};

export type ProductCatalogPlanColumn = { key: string; label: string };
export type ProductCatalogPlanRow = {
  feature: string;
  description?: string;
  values: Record<string, string>;
};
export type ProductCatalogPlanCard = {
  key: "free" | "standard" | "executive";
  name: string;
  priceLabel: string;
  description: string;
  purchasable: boolean;
};
export type ProductCatalogPlansProjection = {
  columns: ProductCatalogPlanColumn[];
  cards: ProductCatalogPlanCard[];
  limitRows: ProductCatalogPlanRow[];
  frequencyRows: ProductCatalogPlanRow[];
  unlimitedNotice: string | null;
};

export class ProductCatalogValidationError extends Error {
  constructor(path: string) {
    super(`Invalid Product Catalog response at ${path}.`);
    this.name = "ProductCatalogValidationError";
  }
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ProductCatalogValidationError(path);
  return value as Record<string, unknown>;
}

function recordOrEmptyListAsRecord(value: unknown, path: string): Record<string, unknown> {
  if (Array.isArray(value)) {
    if (value.length === 0) return {};
    throw new ProductCatalogValidationError(path);
  }
  return record(value, path);
}

function stringValue(value: unknown, path: string): string {
  if (typeof value !== "string") throw new ProductCatalogValidationError(path);
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value !== null && typeof value !== "string") throw new ProductCatalogValidationError(path);
  return value as string | null;
}

function booleanValue(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new ProductCatalogValidationError(path);
  return value;
}

function nonNegativeInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw new ProductCatalogValidationError(path);
  return value;
}

function nullableNonNegativeInteger(value: unknown, path: string): number | null {
  if (value === null) return null;
  return nonNegativeInteger(value, path);
}

function arrayValue(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new ProductCatalogValidationError(path);
  return value;
}

function namedValue(value: unknown, path: string): ProductCatalogNamedValue {
  const source = record(value, path);
  return {
    key: stringValue(source.key, `${path}.key`),
    displayName: stringValue(source.display_name, `${path}.display_name`),
  };
}

function namedValues(value: unknown, path: string): ProductCatalogNamedValue[] {
  return arrayValue(value, path).map((entry, index) => namedValue(entry, `${path}.${index}`));
}

function stringArray(value: unknown, path: string): string[] {
  return arrayValue(value, path).map((entry, index) => stringValue(entry, `${path}.${index}`));
}

function normalizeQuota(value: unknown, path: string): ProductCatalogQuota {
  const source = record(value, path);
  const displayMode = stringValue(source.display_mode, `${path}.display_mode`);
  if (!(["limited", "none", "effectively_unlimited"] as string[]).includes(displayMode)) {
    throw new ProductCatalogValidationError(`${path}.display_mode`);
  }
  return {
    key: stringValue(source.key, `${path}.key`),
    displayName: stringValue(source.display_name, `${path}.display_name`),
    value: nullableNonNegativeInteger(source.value, `${path}.value`),
    displayUnit: stringValue(source.display_unit, `${path}.display_unit`),
    period: nullableString(source.period, `${path}.period`),
    displayPeriod: nullableString(source.display_period, `${path}.display_period`),
    displayMode: displayMode as ProductCatalogQuota["displayMode"],
    displayLabel: stringValue(source.display_label, `${path}.display_label`),
  };
}

function normalizeFeature(value: unknown, path: string): ProductCatalogFeature {
  const source = record(value, path);
  const quotaSource = recordOrEmptyListAsRecord(source.quotas, `${path}.quotas`);
  const quotas = Object.fromEntries(
    Object.entries(quotaSource).map(([key, quota]) => [key, normalizeQuota(quota, `${path}.quotas.${key}`)])
  );
  const subscriptionSource = source.subscription === undefined ? null : record(source.subscription, `${path}.subscription`);
  return {
    key: stringValue(source.key, `${path}.key`),
    displayName: stringValue(source.display_name, `${path}.display_name`),
    description: stringValue(source.description, `${path}.description`),
    enabled: booleanValue(source.enabled, `${path}.enabled`),
    quotas,
    ...(subscriptionSource ? {
      subscription: {
        enabled: booleanValue(subscriptionSource.enabled, `${path}.subscription.enabled`),
        availableFrequencies: namedValues(
          subscriptionSource.available_frequencies,
          `${path}.subscription.available_frequencies`
        ),
      },
    } : {}),
  };
}

function normalizePlan(value: unknown, path: string): ProductCatalogPlan {
  const source = record(value, path);
  const price = record(source.price, `${path}.price`);
  const featureSource = record(source.features, `${path}.features`);
  return {
    key: stringValue(source.key, `${path}.key`),
    displayName: stringValue(source.display_name, `${path}.display_name`),
    description: stringValue(source.description, `${path}.description`),
    price: {
      amount: nullableNonNegativeInteger(price.amount, `${path}.price.amount`),
      currency: stringValue(price.currency, `${path}.price.currency`),
      billingPeriod: nullableString(price.billing_period, `${path}.price.billing_period`),
      taxIncluded: price.tax_included === null ? null : booleanValue(price.tax_included, `${path}.price.tax_included`),
      purchasable: booleanValue(price.purchasable, `${path}.price.purchasable`),
      displayLabel: stringValue(price.display_label, `${path}.price.display_label`),
    },
    features: Object.fromEntries(
      Object.entries(featureSource).map(([key, feature]) => [key, normalizeFeature(feature, `${path}.features.${key}`)])
    ),
  };
}

export function normalizeProductCatalog(value: unknown): ProductCatalog {
  const source = record(value, "data");
  const schemaVersion = stringValue(source.schema_version, "data.schema_version");
  if (schemaVersion !== SUPPORTED_PRODUCT_CATALOG_SCHEMA_VERSION) {
    throw new ProductCatalogValidationError("data.schema_version");
  }
  const capabilities = record(source.capabilities, "data.capabilities");
  const knowledge = record(capabilities.knowledge_library, "data.capabilities.knowledge_library");
  const referenceRequirements = record(knowledge.reference_requirements, "data.capabilities.knowledge_library.reference_requirements");
  const pdf = record(knowledge.pdf, "data.capabilities.knowledge_library.pdf");
  const groupTalk = record(capabilities.group_talk, "data.capabilities.group_talk");
  const calendar = record(capabilities.calendar, "data.capabilities.calendar");
  const sourceScope = record(calendar.source_scope, "data.capabilities.calendar.source_scope");
  const connectionGuidance = record(calendar.connection_guidance, "data.capabilities.calendar.connection_guidance");
  const executiveMemo = record(capabilities.executive_memo, "data.capabilities.executive_memo");
  const noticeSource = recordOrEmptyListAsRecord(source.notices, "data.notices");
  const notices = Object.fromEntries(Object.entries(noticeSource).map(([key, notice]) => [key, stringValue(notice, `data.notices.${key}`)]));

  const plans = arrayValue(source.plans, "data.plans").map((plan, index) => normalizePlan(plan, `data.plans.${index}`));
  if (plans.length === 0) throw new ProductCatalogValidationError("data.plans");

  return {
    schemaVersion,
    plans,
    capabilities: {
      knowledgeLibrary: {
        displayName: stringValue(knowledge.display_name, "data.capabilities.knowledge_library.display_name"),
        description: stringValue(knowledge.description, "data.capabilities.knowledge_library.description"),
        availablePlanKeys: stringArray(knowledge.available_plan_keys, "data.capabilities.knowledge_library.available_plan_keys"),
        availableOnAllPublicPlans: booleanValue(knowledge.available_on_all_public_plans, "data.capabilities.knowledge_library.available_on_all_public_plans"),
        aiReferenceableSourceTypes: namedValues(knowledge.ai_referenceable_source_types, "data.capabilities.knowledge_library.ai_referenceable_source_types"),
        referenceRequirements: {
          requiresAiReadyState: booleanValue(referenceRequirements.requires_ai_ready_state, "data.capabilities.knowledge_library.reference_requirements.requires_ai_ready_state"),
          displayDescription: stringValue(referenceRequirements.display_description, "data.capabilities.knowledge_library.reference_requirements.display_description"),
        },
        pdf: {
          displayName: stringValue(pdf.display_name, "data.capabilities.knowledge_library.pdf.display_name"),
          maxFileSizeBytes: nonNegativeInteger(pdf.max_file_size_bytes, "data.capabilities.knowledge_library.pdf.max_file_size_bytes"),
          maxFileSizeMb: nonNegativeInteger(pdf.max_file_size_mb, "data.capabilities.knowledge_library.pdf.max_file_size_mb"),
          maxPages: nonNegativeInteger(pdf.max_pages, "data.capabilities.knowledge_library.pdf.max_pages"),
        },
      },
      groupTalk: {
        displayName: stringValue(groupTalk.display_name, "data.capabilities.group_talk.display_name"),
        description: stringValue(groupTalk.description, "data.capabilities.group_talk.description"),
        availablePlanKeys: stringArray(groupTalk.available_plan_keys, "data.capabilities.group_talk.available_plan_keys"),
        maxKnowledgeItems: nonNegativeInteger(groupTalk.max_knowledge_items, "data.capabilities.group_talk.max_knowledge_items"),
        knowledgeSelectionTiming: namedValue(groupTalk.knowledge_selection_timing, "data.capabilities.group_talk.knowledge_selection_timing"),
        knowledgeChangeDuringSession: booleanValue(groupTalk.knowledge_change_during_session, "data.capabilities.group_talk.knowledge_change_during_session"),
        knowledgeReusableFromLibrary: booleanValue(groupTalk.knowledge_reusable_from_library, "data.capabilities.group_talk.knowledge_reusable_from_library"),
        userInputMaxCharacters: nonNegativeInteger(groupTalk.user_input_max_characters, "data.capabilities.group_talk.user_input_max_characters"),
      },
      calendar: {
        displayName: stringValue(calendar.display_name, "data.capabilities.calendar.display_name"),
        availablePlanKeys: stringArray(calendar.available_plan_keys, "data.capabilities.calendar.available_plan_keys"),
        availableOnAllPublicPlans: booleanValue(calendar.available_on_all_public_plans, "data.capabilities.calendar.available_on_all_public_plans"),
        usesChatQuota: booleanValue(calendar.uses_chat_quota, "data.capabilities.calendar.uses_chat_quota"),
        readOnly: booleanValue(calendar.read_only, "data.capabilities.calendar.read_only"),
        sourceScope: {
          mainCalendarOnly: booleanValue(sourceScope.main_calendar_only, "data.capabilities.calendar.source_scope.main_calendar_only"),
          supportedSources: namedValues(sourceScope.supported_sources, "data.capabilities.calendar.source_scope.supported_sources"),
          unsupportedSources: namedValues(sourceScope.unsupported_sources, "data.capabilities.calendar.source_scope.unsupported_sources"),
        },
        supportedRanges: namedValues(calendar.supported_ranges, "data.capabilities.calendar.supported_ranges"),
        unsupportedActions: namedValues(calendar.unsupported_actions, "data.capabilities.calendar.unsupported_actions"),
        connectionGuidance: {
          settingsLocation: stringValue(connectionGuidance.settings_location, "data.capabilities.calendar.connection_guidance.settings_location"),
          doNotAssumePlanShortage: booleanValue(connectionGuidance.do_not_assume_plan_shortage, "data.capabilities.calendar.connection_guidance.do_not_assume_plan_shortage"),
          distinguishFailureStates: namedValues(connectionGuidance.distinguish_failure_states, "data.capabilities.calendar.connection_guidance.distinguish_failure_states"),
          hideTechnicalPermissionNames: booleanValue(connectionGuidance.hide_technical_permission_names, "data.capabilities.calendar.connection_guidance.hide_technical_permission_names"),
        },
      },
      executiveMemo: {
        displayName: stringValue(executiveMemo.display_name, "data.capabilities.executive_memo.display_name"),
        maxImagesPerMemo: nonNegativeInteger(executiveMemo.max_images_per_memo, "data.capabilities.executive_memo.max_images_per_memo"),
        quotaDisplayUnit: stringValue(executiveMemo.quota_display_unit, "data.capabilities.executive_memo.quota_display_unit"),
      },
    },
    notices,
  };
}

export function countTrimmedUnicodeCodePoints(value: string): number {
  return Array.from(value.trim()).length;
}

export function getProductCatalogPlan(
  catalog: ProductCatalog,
  planKey: ProductCatalogPlanKey
): ProductCatalogPlan | null {
  return catalog.plans.find((plan) => plan.key === planKey) ?? null;
}

export function getProductCatalogPriceDisplayLabel(
  catalog: ProductCatalog,
  planKey: ProductCatalogPlanKey
): string | null {
  return getProductCatalogPlan(catalog, planKey)?.price.displayLabel ?? null;
}

export function isProductCatalogFeatureAvailable(
  catalog: ProductCatalog,
  planKey: ProductCatalogPlanKey,
  featureKey: ProductCatalogFeatureKey
): boolean {
  return getProductCatalogPlan(catalog, planKey)?.features[featureKey]?.enabled === true;
}

export function getProductCatalogQuotaDisplayLabel(
  catalog: ProductCatalog,
  planKey: ProductCatalogPlanKey,
  featureKey: ProductCatalogFeatureKey,
  quotaKey: string
): string | null {
  const feature = getProductCatalogPlan(catalog, planKey)?.features[featureKey];
  if (!feature?.enabled) return null;
  return feature.quotas[quotaKey]?.displayLabel ?? null;
}

export function getProductCatalogSubscriptionFrequencyDisplayLabel(
  catalog: ProductCatalog,
  planKey: ProductCatalogPlanKey,
  featureKey: ProductCatalogFeatureKey
): string | null {
  const feature = getProductCatalogPlan(catalog, planKey)?.features[featureKey];
  if (!feature?.enabled || !feature.subscription?.enabled) return null;
  const label = feature.subscription.availableFrequencies
    .map((frequency) => frequency.displayName)
    .join("・");
  return label || null;
}

export function projectProductCatalogForPlans(catalog: ProductCatalog): ProductCatalogPlansProjection {
  const columns = catalog.plans.map((plan) => ({ key: plan.key, label: plan.displayName }));
  const cards = catalog.plans
    .filter((plan): plan is ProductCatalogPlan & { key: ProductCatalogPlanCard["key"] } =>
      plan.key === "free" || plan.key === "standard" || plan.key === "executive"
    )
    .map((plan) => ({
      key: plan.key,
      name: plan.displayName,
      priceLabel: plan.price.displayLabel,
      description: plan.description,
      purchasable: plan.price.purchasable,
    }));

  const featureKeys = Array.from(new Set(catalog.plans.flatMap((plan) => Object.keys(plan.features))));
  const limitRows: ProductCatalogPlanRow[] = [];
  const frequencyRows: ProductCatalogPlanRow[] = [];

  featureKeys.forEach((featureKey) => {
    const representative = catalog.plans.map((plan) => plan.features[featureKey]).find(Boolean);
    if (!representative) return;
    const quotaKeys = Array.from(new Set(catalog.plans.flatMap((plan) => Object.keys(plan.features[featureKey]?.quotas ?? {}))));
    if (quotaKeys.length === 0) {
      limitRows.push({
        feature: representative.displayName,
        description: representative.description,
        values: Object.fromEntries(catalog.plans.map((plan) => [plan.key, plan.features[featureKey]?.enabled ? "利用可" : "なし"])),
      });
    } else {
      quotaKeys.forEach((quotaKey) => {
        const quotaRepresentative = catalog.plans
          .map((plan) => plan.features[featureKey]?.quotas[quotaKey])
          .find(Boolean);
        if (!quotaRepresentative) return;
        limitRows.push({
          feature: quotaKeys.length === 1
            ? representative.displayName
            : `${representative.displayName} ${quotaRepresentative.displayName}`,
          description: representative.description,
          values: Object.fromEntries(catalog.plans.map((plan) => {
            const feature = plan.features[featureKey];
            const quota = feature?.quotas[quotaKey];
            return [plan.key, feature?.enabled && quota ? quota.displayLabel : "なし"];
          })),
        });
      });
    }

    if (catalog.plans.some((plan) => plan.features[featureKey]?.subscription !== undefined)) {
      frequencyRows.push({
        feature: `${representative.displayName} 定期配信`,
        values: Object.fromEntries(catalog.plans.map((plan) => {
          const subscription = plan.features[featureKey]?.subscription;
          return [plan.key, subscription?.enabled
            ? subscription.availableFrequencies.map((frequency) => frequency.displayName).join("・")
            : "なし"];
        })),
      });
    }
  });

  return {
    columns,
    cards,
    limitRows,
    frequencyRows,
    unlimitedNotice: catalog.notices.effectively_unlimited ?? null,
  };
}
